import { API } from "@swivl-mono/api-helper";
import { AvailablePhoneNumber, LocationForNumberPurchase, LocationVarKey, NumberPlatform } from "@swivl-mono/types";
import { Button, ButtonColor, ButtonVariant, Input, Select, SelectItem } from "@swivl-mono/ui";
import { useEffect, useMemo, useState } from "react";

import AppsModel from "../../../../Models/Apps/Apps.Model";
import { Member } from "../../../../Models/Member/Member.Current";
import ModalModel from "../../../../Models/Modal/Modal.Model";

type ModalStep = "select_location" | "select_number" | "purchasing" | "success" | "error";

interface Props {
  onSuccess?: (appId: number, phoneNumber: string) => void;
}

interface ParsedSearchTerm {
  areaCode?: string;
  postalCode?: string;
  invalid?: boolean;
}

const ORG_LEVEL_VALUE = "org";

/** Three digits mean an area code; a zip or Canadian postal code is a postal code. */
function parseSearchTerm(term: string): ParsedSearchTerm {
  const value = term.trim();
  if (!value) return {};
  if (/^\d{3}$/.test(value)) return { areaCode: value };
  if (/^\d{5}(-\d{4})?$/.test(value) || /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/.test(value)) return { postalCode: value };
  return { invalid: true };
}

export function PurchaseNumberModal({ onSuccess }: Props) {
  const { twilioSetup } = AppsModel.useState();
  const buysOnParentAccount = !!twilioSetup && !twilioSetup.vars?.[LocationVarKey.TwilioSubaccountSID];

  // Step state
  const [step, setStep] = useState<ModalStep>("select_location");

  // Location selection state
  const [locations, setLocations] = useState<LocationForNumberPurchase[]>([]);
  const [selectedLocationId, setSelectedLocationId] = useState<number | null>(null);
  // Which platform answers the number (ENG-3468, Chuck 2026-09-30): a Snowy org
  // defaults to the bot; every other org to legacy channels, which is what every
  // purchase did before. Staff can flip it either way.
  const [platform, setPlatform] = useState<NumberPlatform>(Member.isSnowyEnabled ? "snowy" : "legacy");
  const [conferenceAppNeeded, setConferenceAppNeeded] = useState(false);
  const [conferenceAppError, setConferenceAppError] = useState("");
  const [existingOutboundAppKept, setExistingOutboundAppKept] = useState(false);
  const [isLoadingLocations, setIsLoadingLocations] = useState(true);

  // Number search state
  const [availableNumbers, setAvailableNumbers] = useState<AvailablePhoneNumber[]>([]);
  const [selectedNumber, setSelectedNumber] = useState<AvailablePhoneNumber | null>(null);
  const [isSearchingNumbers, setIsSearchingNumbers] = useState(false);
  const [searchedPostalCode, setSearchedPostalCode] = useState<string>("");
  const [searchedAreaCode, setSearchedAreaCode] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");

  // Purchase state
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [purchasedPhoneNumber, setPurchasedPhoneNumber] = useState<string>("");
  const [purchasedAppId, setPurchasedAppId] = useState<number | null>(null);

  // Error state
  const [error, setError] = useState<string>("");

  // Load locations on mount
  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    if (!Member.orgId) return;

    setIsLoadingLocations(true);
    setError("");

    try {
      const response = await API.TwilioSetup.getLocationsForNumberPurchase(Member.orgId);
      setLocations(response.locations);

      // Auto-select first location with a postal code
      const locationWithPostal = response.locations.find((loc) => loc.postalCode);
      if (locationWithPostal) {
        setSelectedLocationId(locationWithPostal.id);
      }
    } catch (err: any) {
      setError(err.message || "Failed to load locations");
    } finally {
      setIsLoadingLocations(false);
    }
  };

  // Get selected location details
  const selectedLocation = useMemo(() => {
    return locations.find((loc) => loc.id === selectedLocationId);
  }, [locations, selectedLocationId]);

  // Location dropdown options
  const platformOptions: SelectItem[] = useMemo(
    () => [
      { label: "New system", value: "snowy" },
      { label: "Old system", value: "legacy" }
    ],
    []
  );

  const locationOptions: SelectItem[] = useMemo(() => {
    const options = locations.map((loc) => ({
      label: loc.groupName ? `${loc.groupName} - ${loc.name}` : loc.name,
      value: String(loc.id)
    }));
    return [{ label: "Whole organization (no location)", value: ORG_LEVEL_VALUE }, ...options];
  }, [locations]);

  const parsedSearch = parseSearchTerm(searchTerm);
  const orgHasPostalCode = !!twilioSetup?.vars?.[LocationVarKey.PostalCode];

  // Search for available numbers
  const searchNumbers = async () => {
    if (!Member.orgId || parsedSearch.invalid) return;

    setIsSearchingNumbers(true);
    setError("");
    setAvailableNumbers([]);
    setSelectedNumber(null);

    try {
      const response = await API.TwilioSetup.searchAvailableNumbers(Member.orgId, {
        locationId: selectedLocationId ?? undefined,
        postalCode: parsedSearch.postalCode,
        areaCode: parsedSearch.areaCode,
        limit: 20
      });

      setAvailableNumbers(response.numbers);
      setSearchedPostalCode(response.searchedPostalCode);
      setSearchedAreaCode(response.searchedAreaCode ?? "");
      setStep("select_number");

      if (response.numbers.length === 0) {
        setError("No available numbers found. Try a different zip, area code or location.");
      }
    } catch (err: any) {
      setError(err.message || "Failed to search for available numbers");
    } finally {
      setIsSearchingNumbers(false);
    }
  };

  // Purchase the selected number
  const purchaseNumber = async () => {
    if (!Member.orgId || !selectedNumber) return;

    if (
      buysOnParentAccount &&
      !window.confirm(
        "A subaccount has not been selected for this org. Choosing OK will buy a number on our parent account. If the org has been A2P approved, choose Cancel and select their subaccount."
      )
    ) {
      return;
    }

    setIsPurchasing(true);
    setStep("purchasing");
    setError("");

    try {
      const response = await API.TwilioSetup.purchaseNumber(Member.orgId, {
        phoneNumber: selectedNumber.phoneNumber,
        locationId: selectedLocationId || undefined,
        platform,
        // Staff confirmed the parent-account warning above; the server refuses without this.
        useParentAccount: buysOnParentAccount
      });

      if (response.success && response.appId && response.phoneNumber) {
        setPurchasedPhoneNumber(response.phoneNumber);
        setPurchasedAppId(response.appId);
        setConferenceAppNeeded(response.conferenceAppNeeded === true);
        setConferenceAppError(response.conferenceAppError ?? "");
        setExistingOutboundAppKept(response.existingOutboundAppKept === true);
        setStep("success");

        // Reload apps to reflect the new purchase
        await AppsModel.load(true);

        if (onSuccess) {
          onSuccess(response.appId, response.phoneNumber);
        }
      } else {
        setError(response.error || "Failed to purchase number");
        setStep("error");
      }
    } catch (err: any) {
      setError(err.message || "Failed to purchase number");
      setStep("error");
    } finally {
      setIsPurchasing(false);
    }
  };

  // Format phone number for display
  const formatPhoneNumber = (phone: string) => {
    // Format E.164 to readable format
    if (phone.startsWith("+1") && phone.length === 12) {
      return `+1 (${phone.slice(2, 5)}) ${phone.slice(5, 8)}-${phone.slice(8)}`;
    }
    return phone;
  };

  // Render step content
  const renderStepContent = () => {
    switch (step) {
      case "select_location":
        return (
          <div className="w-full">
            <p className="mb-4 text-sm text-gray-600">Choose a location, or the whole organization, to search for available phone numbers.</p>

            <div className="mb-4">
              <Select
                label="This number is for"
                options={platformOptions}
                selectedValues={[platform]}
                onChange={(vals) => setPlatform(vals[0] === "legacy" ? "legacy" : "snowy")}
                hideClear
              />
              <p className="mt-1 text-xs text-gray-500">
                {platform === "snowy" ? "Calls and texts go to the new system." : "Calls and texts go to the old system."}
              </p>
            </div>

            <div className="mb-4">
              <Select
                label="Location"
                options={locationOptions}
                selectedValues={[selectedLocationId !== null ? String(selectedLocationId) : ORG_LEVEL_VALUE]}
                onChange={(vals) => setSelectedLocationId(vals[0] && vals[0] !== ORG_LEVEL_VALUE ? Number(vals[0]) : null)}
                placeholder={isLoadingLocations ? "Loading locations..." : "Select a location"}
                disabled={isLoadingLocations}
                isSearchable
                hideClear
              />
              {selectedLocation && (
                <div className="mt-1 text-xs text-gray-500">
                  <p>Postal code: {selectedLocation.postalCode || "Not set"}</p>
                  {selectedLocation.phone && <p>Location phone: {selectedLocation.phone}</p>}
                </div>
              )}
            </div>

            <div className="mb-4">
              <Input
                label="Zip or area code (optional)"
                value={searchTerm}
                placeholder="e.g. 80202 or 720"
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <p className="mt-1 text-xs text-gray-500">
                Leave blank to search near {selectedLocation ? "the location's" : "the organization's"} zip. Three digits search that area code
                instead.
              </p>
              {parsedSearch.invalid && <p className="mt-1 text-xs text-red-500">Enter a 3-digit area code or a zip code.</p>}
            </div>

            {!searchTerm.trim() && !(selectedLocation ? selectedLocation.postalCode : orgHasPostalCode) && (
              <p className="mb-4 text-sm text-orange-600">
                {selectedLocation ? "This location has no postal code" : "The organization has no postal code"}
                {orgHasPostalCode || selectedLocation?.postalCode
                  ? ", so the search will use the organization's postal code unless you type a zip or area code above."
                  : ". Type a zip or area code above to search."}
              </p>
            )}
          </div>
        );

      case "select_number":
        return (
          <div className="w-full">
            <p className="mb-2 text-sm text-gray-600">
              {searchedAreaCode ? "Searching area code: " : "Searching near postal code: "}
              <span className="font-medium">{searchedAreaCode || searchedPostalCode}</span>
            </p>
            <p className="mb-4 text-sm text-gray-600">Select a phone number to purchase:</p>

            {availableNumbers.length > 0 ? (
              <div className="mb-4 max-h-64 overflow-y-auto rounded-md border">
                {availableNumbers.map((num, index) => (
                  <div
                    key={num.phoneNumber}
                    className={`cursor-pointer border-b p-3 last:border-b-0 hover:bg-gray-50 ${
                      selectedNumber?.phoneNumber === num.phoneNumber ? "border-blue-200 bg-blue-50" : ""
                    }`}
                    onClick={() => setSelectedNumber(num)}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">{formatPhoneNumber(num.phoneNumber)}</p>
                        <p className="text-sm text-gray-600">
                          {num.locality}
                          {num.region ? `, ${num.region}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="radio"
                          checked={selectedNumber?.phoneNumber === num.phoneNumber}
                          onChange={() => setSelectedNumber(num)}
                          className="h-4 w-4 text-blue-600"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mb-4 rounded-md bg-orange-50 p-4">
                <p className="text-orange-700">No available numbers found. Go back and try a different zip or area code.</p>
              </div>
            )}

            {selectedNumber && (
              <div className="mb-4 rounded-md bg-blue-50 p-3">
                <p className="text-sm font-medium">Selected number:</p>
                <p className="text-lg font-bold text-blue-700">{formatPhoneNumber(selectedNumber.phoneNumber)}</p>
                <p className="text-sm text-gray-600">
                  {selectedNumber.locality}
                  {selectedNumber.region ? `, ${selectedNumber.region}` : ""}
                </p>
              </div>
            )}
          </div>
        );

      case "purchasing":
        return (
          <div className="w-full py-8 text-center">
            <div className="mx-auto mb-4 h-12 w-12 animate-spin rounded-full border-b-2 border-blue-600"></div>
            <p className="text-lg font-medium">Purchasing number...</p>
            <p className="mt-2 text-sm text-gray-600">{formatPhoneNumber(selectedNumber?.phoneNumber || "")}</p>
            <p className="mt-4 text-sm text-gray-500">Please wait while we configure your new phone number.</p>
          </div>
        );

      case "success":
        return (
          <div className="w-full py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <p className="text-lg font-medium text-green-700">Number purchased successfully!</p>
            <p className="mt-2 text-2xl font-bold">{formatPhoneNumber(purchasedPhoneNumber)}</p>
            <p className="mt-4 text-sm text-gray-600">
              {platform === "snowy" ? "Calls and texts to it go to the new system." : "Calls and texts to it go to the old system."}
            </p>
            {existingOutboundAppKept && (
              <p className="mt-2 text-sm text-orange-600">
                {selectedLocation?.name ?? "This location"}&apos;s Outbound Twilio App was not updated by buying this additional number.
              </p>
            )}
            {conferenceAppNeeded && (
              <p className="mt-2 text-sm text-orange-600">
                The number was bought, but the subaccount&apos;s new-system setup could not be finished
                {conferenceAppError ? ` (${conferenceAppError})` : ""}. Open its card and press <strong>Finish new system setup</strong>, or calls to
                it will not reach the new system.
              </p>
            )}
          </div>
        );

      case "error":
        return (
          <div className="w-full py-8 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <svg className="h-8 w-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <p className="text-lg font-medium text-red-700">Failed to purchase number</p>
            <p className="mt-2 text-sm text-gray-600">{error}</p>
            <p className="mt-4 text-sm text-gray-500">The number may no longer be available. Please try searching again.</p>
          </div>
        );
    }
  };

  // Render action buttons based on step
  const renderActions = () => {
    switch (step) {
      case "select_location":
        const hasSearchTarget = !!searchTerm.trim() || !!selectedLocation?.postalCode || orgHasPostalCode;
        const canSearch = hasSearchTarget && !parsedSearch.invalid && !isSearchingNumbers;
        return (
          <>
            <Button variant={ButtonVariant.GHOST} onClick={() => ModalModel.hideModal()}>
              Cancel
            </Button>
            <Button color={ButtonColor.ORANGE} onClick={searchNumbers} disabled={!canSearch} loading={isSearchingNumbers}>
              {isSearchingNumbers ? "Searching..." : "Search Numbers"}
            </Button>
          </>
        );

      case "select_number":
        return (
          <>
            <Button
              variant={ButtonVariant.GHOST}
              onClick={() => {
                setStep("select_location");
                setAvailableNumbers([]);
                setSelectedNumber(null);
              }}
            >
              Back
            </Button>
            <Button variant={ButtonVariant.GHOST} onClick={searchNumbers} disabled={isSearchingNumbers}>
              {isSearchingNumbers ? "Searching..." : "Search Again"}
            </Button>
            <Button color={ButtonColor.ORANGE} onClick={purchaseNumber} disabled={!selectedNumber}>
              Purchase Number
            </Button>
          </>
        );

      case "purchasing":
        return null;

      case "success":
        return (
          <Button color={ButtonColor.ORANGE} onClick={() => ModalModel.hideModal()}>
            Done
          </Button>
        );

      case "error":
        return (
          <>
            <Button variant={ButtonVariant.GHOST} onClick={() => ModalModel.hideModal()}>
              Cancel
            </Button>
            <Button
              color={ButtonColor.ORANGE}
              onClick={() => {
                setStep("select_number");
                setError("");
                searchNumbers();
              }}
            >
              Search Again
            </Button>
          </>
        );
    }
  };

  return (
    <div className="w-[500px]">
      {/* Progress indicator */}
      {(step === "select_location" || step === "select_number") && (
        <div className="mb-4 flex items-center justify-center">
          <div className={`flex items-center ${step === "select_location" ? "text-blue-600" : "text-gray-400"}`}>
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                step === "select_location" ? "bg-blue-600 text-white" : "bg-gray-200"
              }`}
            >
              1
            </div>
            <span className="ml-2 text-sm font-medium">Location</span>
          </div>
          <div className="mx-2 h-px w-8 bg-gray-300"></div>
          <div className={`flex items-center ${step === "select_number" ? "text-blue-600" : "text-gray-400"}`}>
            <div
              className={`flex h-8 w-8 items-center justify-center rounded-full ${
                step === "select_number" ? "bg-blue-600 text-white" : "bg-gray-200"
              }`}
            >
              2
            </div>
            <span className="ml-2 text-sm font-medium">Number</span>
          </div>
        </div>
      )}

      {/* Error display */}
      {error && step !== "error" && <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-red-700">{error}</div>}

      {/* Step content */}
      {renderStepContent()}

      {/* Action buttons */}
      <div className="mt-6 flex justify-end gap-2 border-t pt-4">{renderActions()}</div>
    </div>
  );
}

// Helper function to show the modal
export function showPurchaseNumberModal(onSuccess?: (appId: number, phoneNumber: string) => void) {
  ModalModel.showModalWithContent("Buy a Phone Number", <PurchaseNumberModal onSuccess={onSuccess} />, undefined, 560, false);
}
